import React, { useState, useEffect } from 'react';
import api, { payrollAPI, companiesAPI, employeesAPI } from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import ExportButton from '../components/ExportButton';
import { 
  DollarSign, 
  Play,
  FileText,
  Download,
  Building2,
  Users,
  Loader2
} from 'lucide-react';

const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

const Payroll = () => {
  const [companies, setCompanies] = useState([]);
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processDialogOpen, setProcessDialogOpen] = useState(false);
  const [payslipDialogOpen, setPayslipDialogOpen] = useState(false);
  const [selectedPayslip, setSelectedPayslip] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [processData, setProcessData] = useState({
    company_id: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear()
  });
  const [filterCompany, setFilterCompany] = useState('');
  const [filterMonth, setFilterMonth] = useState('');
  const [filterYear, setFilterYear] = useState('');

  useEffect(() => {
    fetchData();
  }, [filterCompany, filterMonth, filterYear]);

  const fetchData = async () => {
    try {
      const [companiesRes, payslipsRes] = await Promise.all([
        companiesAPI.getAll(),
        payrollAPI.getPayslips({
          ...(filterCompany && { company_id: filterCompany }),
          ...(filterMonth && { month: parseInt(filterMonth) }),
          ...(filterYear && { year: parseInt(filterYear) })
        })
      ]);
      setCompanies(companiesRes.data);
      setPayslips(payslipsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessPayroll = async () => {
    if (!processData.company_id) {
      toast.error('Please select a company');
      return;
    }

    setProcessing(true);
    try {
      const res = await payrollAPI.process(processData);
      toast.success(`Processed payroll for ${res.data.payslips?.length || 0} employees`);
      setProcessDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process payroll');
    } finally {
      setProcessing(false);
    }
  };

  const viewPayslip = (payslip) => {
    setSelectedPayslip(payslip);
    setPayslipDialogOpen(true);
  };

  const getCompanyName = (companyId) => {
    const company = companies.find(c => c.id === companyId);
    return company?.name || 'Unknown';
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2
    }).format(amount);
  };

  const totalPayroll = payslips.reduce((sum, p) => sum + (p.net_salary || 0), 0);
  const totalGross = payslips.reduce((sum, p) => sum + (p.gross_salary || 0), 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Payroll</h1>
          <p className="text-muted-foreground">Process and manage payroll across all subsidiaries</p>
        </div>
        <div className="flex gap-2">
        <ExportButton endpoint="/reports/payroll/export" filename="payroll" testId="export-payroll" />
        <Dialog open={processDialogOpen} onOpenChange={setProcessDialogOpen}>
          <DialogTrigger asChild>
            <Button className="btn-hover" data-testid="process-payroll-btn">
              <Play size={18} className="mr-2" />
              Process Payroll
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Process Payroll</DialogTitle>
              <DialogDescription>
                Select company and period to process payroll
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Company</Label>
                <Select 
                  value={processData.company_id} 
                  onValueChange={(v) => setProcessData({ ...processData, company_id: v })}
                >
                  <SelectTrigger data-testid="process-company-select">
                    <SelectValue placeholder="Select company" />
                  </SelectTrigger>
                  <SelectContent>
                    {companies.map(company => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name} ({company.country})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Month</Label>
                  <Select 
                    value={processData.month.toString()} 
                    onValueChange={(v) => setProcessData({ ...processData, month: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="process-month-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {months.map((month, index) => (
                        <SelectItem key={month} value={(index + 1).toString()}>
                          {month}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Year</Label>
                  <Select 
                    value={processData.year.toString()} 
                    onValueChange={(v) => setProcessData({ ...processData, year: parseInt(v) })}
                  >
                    <SelectTrigger data-testid="process-year-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[2023, 2024, 2025, 2026].map(year => (
                        <SelectItem key={year} value={year.toString()}>
                          {year}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setProcessDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleProcessPayroll} disabled={processing} data-testid="confirm-process-btn">
                {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Process Payroll
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                <FileText className="text-primary" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Payslips</p>
                <p className="text-2xl font-bold">{payslips.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-emerald-500/10 flex items-center justify-center">
                <DollarSign className="text-emerald-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Gross</p>
                <p className="text-2xl font-bold">{formatCurrency(totalGross)}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-blue-500/10 flex items-center justify-center">
                <DollarSign className="text-blue-500" size={20} />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Net</p>
                <p className="text-2xl font-bold">{formatCurrency(totalPayroll)}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <Select value={filterCompany} onValueChange={setFilterCompany}>
          <SelectTrigger className="w-48" data-testid="filter-payroll-company">
            <Building2 size={16} className="mr-2" />
            <SelectValue placeholder="All Companies" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Companies</SelectItem>
            {companies.map(company => (
              <SelectItem key={company.id} value={company.id}>
                {company.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger className="w-40" data-testid="filter-payroll-month">
            <SelectValue placeholder="All Months" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Months</SelectItem>
            {months.map((month, index) => (
              <SelectItem key={month} value={(index + 1).toString()}>
                {month}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={filterYear} onValueChange={setFilterYear}>
          <SelectTrigger className="w-32" data-testid="filter-payroll-year">
            <SelectValue placeholder="All Years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Years</SelectItem>
            {[2023, 2024, 2025, 2026].map(year => (
              <SelectItem key={year} value={year.toString()}>
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Payslips Table */}
      <Card>
        <CardHeader>
          <CardTitle>Payslips</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead>Company</TableHead>
                <TableHead>Period</TableHead>
                <TableHead>Gross</TableHead>
                <TableHead>Deductions</TableHead>
                <TableHead>Net</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.length > 0 ? (
                payslips.map((payslip) => (
                  <TableRow key={payslip.id} data-testid={`payslip-row-${payslip.id}`}>
                    <TableCell className="font-medium">
                      <div>
                        <p>{payslip.employee_name}</p>
                        <p className="text-xs text-muted-foreground">{payslip.employee_code}</p>
                      </div>
                    </TableCell>
                    <TableCell>{getCompanyName(payslip.company_id)}</TableCell>
                    <TableCell>
                      {months[payslip.month - 1]} {payslip.year}
                    </TableCell>
                    <TableCell>{formatCurrency(payslip.gross_salary, payslip.currency)}</TableCell>
                    <TableCell className="text-red-500">
                      -{formatCurrency(
                        Object.values(payslip.deductions || {}).reduce((sum, v) => sum + v, 0),
                        payslip.currency
                      )}
                    </TableCell>
                    <TableCell className="font-semibold">
                      {formatCurrency(payslip.net_salary, payslip.currency)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => viewPayslip(payslip)}
                        data-testid={`view-payslip-${payslip.id}`}
                      >
                        <FileText size={16} className="mr-1" />
                        View
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label="Download payslip PDF"
                        title="Download PDF"
                        onClick={async () => {
                          try {
                            const res = await api.get(`/payslips/${payslip.id}/pdf`, { responseType: 'blob' });
                            const url = window.URL.createObjectURL(new Blob([res.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.download = `payslip_${payslip.employee_code}_${payslip.year}_${payslip.month}.pdf`;
                            link.click();
                            window.URL.revokeObjectURL(url);
                          } catch { toast.error('Download failed'); }
                        }}
                        data-testid={`download-payslip-${payslip.id}`}
                      >
                        <Download size={16} />
                      </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                    No payslips found. Process payroll to generate payslips.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Payslip Detail Dialog */}
      <Dialog open={payslipDialogOpen} onOpenChange={setPayslipDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Payslip Details</DialogTitle>
            <DialogDescription>
              {selectedPayslip && `${months[selectedPayslip.month - 1]} ${selectedPayslip.year}`}
            </DialogDescription>
          </DialogHeader>
          {selectedPayslip && (
            <div className="space-y-6">
              <div className="p-4 bg-muted rounded-lg">
                <h4 className="font-semibold mb-2">{selectedPayslip.employee_name}</h4>
                <p className="text-sm text-muted-foreground">{selectedPayslip.employee_code}</p>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Earnings</h4>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span>Basic Salary</span>
                    <span className="font-medium">
                      {formatCurrency(selectedPayslip.basic, selectedPayslip.currency)}
                    </span>
                  </div>
                  {Object.entries(selectedPayslip.allowances || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between">
                      <span className="capitalize">{key.replace('_', ' ')}</span>
                      <span className="font-medium">
                        {formatCurrency(value, selectedPayslip.currency)}
                      </span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t font-semibold">
                    <span>Gross Salary</span>
                    <span>{formatCurrency(selectedPayslip.gross_salary, selectedPayslip.currency)}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 className="font-semibold mb-3">Deductions</h4>
                <div className="space-y-2">
                  {Object.entries(selectedPayslip.deductions || {}).map(([key, value]) => (
                    <div key={key} className="flex justify-between text-red-500">
                      <span className="capitalize">{key.replace('_', ' ')}</span>
                      <span className="font-medium">-{formatCurrency(value, selectedPayslip.currency)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between pt-2 border-t font-semibold text-red-500">
                    <span>Total Deductions</span>
                    <span>
                      -{formatCurrency(
                        Object.values(selectedPayslip.deductions || {}).reduce((sum, v) => sum + v, 0),
                        selectedPayslip.currency
                      )}
                    </span>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-primary/10 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Net Salary</span>
                  <span className="text-2xl font-bold">
                    {formatCurrency(selectedPayslip.net_salary, selectedPayslip.currency)}
                  </span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Payroll;
